import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { paymentLinkService, PaymentLinkView } from '../../api/services';
import { LinkIcon, ArrowPathIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';

interface Props {
  documentId: number;
  className?: string;
}

const statusColor: Record<PaymentLinkView['status'], string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  PAID:    'bg-green-100 text-green-800',
  FAILED:  'bg-red-100 text-red-800',
  EXPIRED: 'bg-gray-100 text-gray-600',
};

const PaymentLinksSection: React.FC<Props> = ({ documentId, className }) => {
  const [links, setLinks] = useState<PaymentLinkView[]>([]);
  const [provider, setProvider] = useState<PaymentLinkView['provider']>('KONNECT');
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    paymentLinkService.listForDocument(documentId)
      .then(setLinks)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur chargement'));

  useEffect(() => { load(); }, [documentId]);

  const create = async () => {
    setBusy(true);
    try {
      await paymentLinkService.create(documentId, provider, sendEmail, email || undefined);
      toast.success('Lien de paiement créé');
      setEmail(''); setSendEmail(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };

  const refund = async (id: number) => {
    const reason = window.prompt('Motif du remboursement (optionnel) ?') || undefined;
    try {
      await paymentLinkService.refund(id, undefined, reason);
      toast.success('Remboursement effectué');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    }
  };

  const copy = (url: string) => {
    navigator.clipboard.writeText(url).then(() => toast.success('URL copiée'));
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 ${className || ''}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <LinkIcon className="h-4 w-4" /> Liens de paiement
        </h3>
      </div>

      <div className="flex gap-2 flex-wrap items-end mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Fournisseur</label>
          <select value={provider} onChange={(e) => setProvider(e.target.value as PaymentLinkView['provider'])}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
            <option value="KONNECT">Konnect</option>
            <option value="CLICTOPAY">Clictopay (STB/BNA)</option>
            <option value="FLOUCI">Flouci</option>
          </select>
        </div>
        <div className="flex-1 min-w-40">
          <label className="block text-xs text-gray-500 mb-1">Email client (optionnel)</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </div>
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
          Envoyer par email
        </label>
        <button onClick={create} disabled={busy}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
          Créer
        </button>
      </div>

      {links.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucun lien créé.</p>
      ) : (
        <ul className="space-y-2">
          {links.map((l) => (
            <li key={l.id} className="flex items-center gap-2 text-sm border border-gray-100 rounded-lg p-2">
              <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${statusColor[l.status]}`}>{l.status}</span>
              <span className="text-xs text-gray-500">{l.provider}</span>
              <a href={l.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-blue-600 hover:underline">
                {l.url}
              </a>
              <button onClick={() => copy(l.url)} title="Copier" className="text-gray-400 hover:text-gray-700">
                <DocumentDuplicateIcon className="h-4 w-4" />
              </button>
              {l.status === 'PAID' && (
                <button onClick={() => refund(l.id)} title="Rembourser"
                        className="text-red-600 hover:text-red-800 text-xs font-semibold flex items-center gap-1">
                  <ArrowPathIcon className="h-3 w-3" /> Refund
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PaymentLinksSection;
